import { useMemo } from 'react';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import BackIcon from '../../assets/logo/BackIcon';
import NestedModifiers from './nested/NestedModifiers';
import { BasketLineItemModifier } from '../../store/types';
import ModifierToggleButton from './ModifierToggleButton';
import RadioButton from '../buttons/RadioButton';
import { CompiledModifier, CompiledProduct } from '../../providers/MenuProvider';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  data: CompiledModifier | CompiledProduct;
  quantity: number;
  isSelected: boolean;
  hasMultiply: boolean;
  isSingleSelect: boolean;
  toggle: (modifier: CompiledModifier | CompiledProduct) => void;
  adjustQuantity: (step: number) => void;
  selectedModifiers: BasketLineItemModifier[];
  isIncreasable: boolean;
  isUpsell?: boolean;
}

export default function Modifier(props: Props) {
  const {
    data,
    isSelected,
    isSingleSelect,
    toggle,
    quantity,
    adjustQuantity,
    selectedModifiers,
    isIncreasable,
    hasMultiply,
    isUpsell,
  } = props;
  const { formatCurrency } = useAppMethods();
  const theme = useTheme();
  const canBeIncreased = (isIncreasable && !isSelected) || (isSelected && isIncreasable && hasMultiply);
  const hasEditButton = !isSelected && !!data?.modifier_group_ids?.length;

  const hasNestedModifiers = isSelected && !!data?.modifier_group_ids?.length;
  const hasImage = isUpsell && 'thumbnail_url' in data && !!data.thumbnail_url;

  function toggleModifier(): void {
    if (isSelected && !hasNestedModifiers) {
      return;
    }

    if (!data.is_available) {
      return;
    }

    toggle(data);
  }

  function getPrice() {
    const isDiscount: boolean = data.base_price > data.actual_price;

    return data.is_available ? (
      <div className="flex flex-row items-center my-auto flex-shrink-0">
        {isDiscount && (
          <p className={priceStyle(isDiscount, data.is_available)}>
            + {formatCurrency(data.base_price * (quantity || 1))}
          </p>
        )}
        <p className={highlightedPriceStyle}>+ {formatCurrency(data.actual_price * (quantity || 1))}</p>
      </div>
    ) : null;
  }

  const modifierGroups = useMemo(
    () =>
      data.modifier_groups?.filter((modifierGroup) =>
        selectedModifiers?.some((modifier) => modifier.modifier_group_id === modifierGroup.id),
      ),
    [selectedModifiers, data.modifier_groups],
  );

  return (
    <button
      className={classnames('w-full', {
        'touchable-highlight': data.is_available,
      })}
      onClick={toggleModifier}
    >
      <div className={classnames(container)}>
        <div className={classnames('flex-1 my-auto')}>
          <p
            className={classnames('text-left text-sm', {
              'opacity-25': !data.is_available,
              'flex items-center gap-6': hasImage,
            })}
          >
            {hasImage && <img className="size-12 rounded-lg" src={data.thumbnail_url} alt="Logo" />}
            {data.name}
          </p>
        </div>
        <div className={classnames('my-auto flex items-center gap-4')}>
          <span>{getPrice()}</span>
          {isSingleSelect ? (
            <RadioButton
              isSelected={isSelected}
              className="border-brand-text-secondary w-[20px] h-[20px]"
              innerClassName="w-[12px] h-[12px]"
            />
          ) : (
            <ModifierToggleButton
              isIncreasable={canBeIncreased}
              isSelected={isSelected}
              quantity={quantity}
              isUpsell={isUpsell}
              increase={() => {
                if (isSelected) {
                  adjustQuantity(1);
                  return;
                }

                toggleModifier();
              }}
              decrease={() => adjustQuantity(-1)}
            />
          )}
          {hasEditButton && <BackIcon className="size-3 rotate-180" color={theme.color.brand.text.secondary} />}
        </div>
      </div>
      {hasNestedModifiers && <NestedModifiers data={modifierGroups} selectedModifiers={selectedModifiers} />}
    </button>
  );
}

const container =
  "flex flex-row w-full my-[8px] before:relative after:content-[''] after:absolute after:-top-0 after:-bottom-0";

const priceStyle = (isDiscount?: boolean, isAvailable?: boolean) =>
  classnames('text-brand-text-secondary line-clamp-1 text-right text-sm', {
    'line-through': isDiscount,
    'opacity-25': !isAvailable,
  });

const highlightedPriceStyle = 'text-brand-text-primary text-right text-sm font-medium line-clamp-1 ml-xs';
